import type {
  ArticleData,
  Dashboard,
  DashboardStats,
  Stuck,
} from "@/shared/api/types";
import type { MappedProduct, MappedStuck, MappedArticleData } from "../types";

interface DashboardCard {
  id: number;
  title: string;
  money: number;
  earnings: number;
  isPercentage: boolean;
  goodIfPositive: boolean;
  moneyDecimals?: number;
}

const absoluteDelta = (current: number, old: number | null): number => {
  if (old == null || !isFinite(current) || !isFinite(old)) return 0;
  return current - old;
};

export function mapDashboardsToCards(dashboards: Dashboard[]): DashboardCard[] {
  if (!dashboards.length) return [];

  const latestDashboard = dashboards[0];
  const currentData = latestDashboard.current_data;
  const oldData = latestDashboard.old_data;

  if (!currentData) return [];

  return [
    {
      id: 1,
      title: "Заказы",
      money: currentData.orders_sum,
      earnings: absoluteDelta(
        currentData.orders_sum,
        oldData?.orders_sum ?? null,
      ),
      isPercentage: false,
      goodIfPositive: true,
      moneyDecimals: 0,
    },
    {
      id: 2,
      title: "ДРР",
      money: currentData.drr * 100,
      earnings: Number(
        (absoluteDelta(currentData.drr, oldData?.drr ?? null) * 100).toFixed(2),
      ),
      isPercentage: true,
      goodIfPositive: false,
    },
    {
      id: 3,
      title: "CTR",
      money: currentData.ctr * 100,
      earnings: Number(
        (absoluteDelta(currentData.ctr, oldData?.ctr ?? null) * 100).toFixed(2),
      ),
      isPercentage: true,
      goodIfPositive: true,
    },
    {
      id: 4,
      title: "CPC",
      money: currentData.cpc,
      earnings: Number(
        absoluteDelta(currentData.cpc, oldData?.cpc ?? null).toFixed(2),
      ),
      isPercentage: false,
      goodIfPositive: false,
      moneyDecimals: 2,
    },
  ];
}

export function mapStatsToProducts(stats: DashboardStats[]): MappedProduct[] {
  return stats.map((stat, index) => ({
    id: `product-${stat.nm_id || index}`,
    name: stat.vendor_code,
    ordersCount: stat.orders_count,
    ordersAmount: stat.orders_sum.toFixed(0),
    drr: (stat.drr * 100).toFixed(1),
    ctr: (stat.ctr * 100).toFixed(2),
    cpc: stat.cpc.toFixed(1),
  }));
}

function mapStuckItem(item: ArticleData): MappedArticleData {
  return {
    nm_id: item.nm_id,
    vendor_code:
      item.vendor_code.length > 26
        ? `${item.vendor_code.slice(0, 26)}...`
        : item.vendor_code,
    ordersCount: item.orders_count,
    ordersAmount: item.order_sum_rub.toFixed(0),
    drr: ((item.sum / item.order_sum_rub) * 100).toFixed(1),
    ctr: ((item.clicks / item.views) * 100).toFixed(1),
    cpc: (item.sum / item.clicks).toFixed(1),
  };
}

export function mapApiToDashboardStucks(stuckLists: Stuck[]): MappedStuck[] {
  return stuckLists.map((stuck) => ({
    id: stuck.imt_id.toString(),
    ordersAmount: stuck.imt_orders_sum.toFixed(0),
    ordersCount: stuck.imt_orders_count,
    drr: (stuck.imt_drr * 100).toFixed(1),
    ctr: (stuck.imt_ctr * 100).toFixed(1),
    cpc: stuck.imt_cpc.toFixed(1),
    items: (stuck.nms ?? []).map(mapStuckItem),
  }));
}
